<script setup lang="ts">
import type { BookAndArticle } from '~/utils/solr/literature'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui'
import { useFetchBookPage } from '~/composables/useFetchBookPage'
import { extractLabel } from '#shared/utils/solr-facet'
import { formatLanguageCode } from '~/utils/string'

const { book } = defineProps<{
  book: BookAndArticle
}>()

const bookPage = useFetchBookPage({
  // We need the wildcard because pages have ids like `isbn-1234567890123/index/side-852`
  id: `${book.id}/*`,
  sort: 'pageNumber desc',
  rows: '1',
})

const highestPageDoc = computed(() => bookPage.data.value?.response.docs[0])

const kolofonItems = computed(() => [
  { label: 'Tittel', value: book.title },
  { label: 'Utgivelsesår', value: book.year },
  { label: 'Språk', value: formatLanguageCode(book.language, true) },
  { label: 'Forfatter', value: book.author?.join(', ') },
  { label: 'Rettsområde', value: book.legal_areas?.map(extractLabel).join(', ') },
  { label: 'Sidetall', value: highestPageDoc.value?.pageNumberString },
  { label: 'Utgiver', value: book.publisher?.[0] },
  { label: 'Utgivelsessted', value: book.placeOfPublication },
  { label: 'ISBN', value: book.isbn?.[0] },
].filter(({ value }) => !!value))

const isOpen = ref<boolean>(true)
</script>

<template>
  <CollapsibleRoot v-model:open="isOpen" class="bg-surface-primary rounded-lg w-full">
    <CollapsibleTrigger class="flex items-center justify-between p-s w-full cursor-pointer">
      <p>Kolofon</p>
      <div class="flex items-center justify-center p-3xs">
        <Icon
          :icon="faChevronDown"
          class="transition-transform duration-300"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </CollapsibleTrigger>

    <CollapsibleContent class="CollapsibleContent overflow-hidden">
      <div class="grid grid-cols-[auto_1fr] gap-x-xl md:gap-x-2xl lg:gap-x-5xl gap-y-m pt-l pb-xl px-2xl">
        <template v-for="item in kolofonItems" :key="item.label">
          <span class="text-s font-bold">{{ item.label }}</span>
          <span class="text-s">{{ item.value }}</span>
        </template>
      </div>
    </CollapsibleContent>
  </CollapsibleRoot>
</template>

<style scoped>
.CollapsibleContent[data-state='open'] {
  animation: slideDown 300ms ease-out;
}

.CollapsibleContent[data-state='closed'] {
  animation: slideUp 300ms ease-in;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--reka-collapsible-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-collapsible-content-height);
  }
  to {
    height: 0;
  }
}
</style>
